import { useState, useCallback } from 'react';
import { useCreateVenue } from '../../hooks/useVenues';
import { useUsers } from '../../hooks/useUsers';
import { GoogleAddressAutocomplete } from '../common/GoogleAddressAutocomplete';
import { PhoneInput } from '../common/PhoneInput';

interface CreateVenuePanelProps {
  onClose: () => void;
  onCreated: () => void;
}

interface FormState {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  contactId: string;
  capacity: string;
  priceRange: string;
  notes: string;
}

const initialForm: FormState = {
  name: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  phone: '',
  email: '',
  contactId: '',
  capacity: '',
  priceRange: '',
  notes: '',
};

export function CreateVenuePanel({ onClose, onCreated }: CreateVenuePanelProps) {
  const createVenue = useCreateVenue();
  const { data: users = [] } = useUsers({ isActive: true });
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState('');

  const isValid = form.name.trim() !== '';

  const handleCreate = useCallback(() => {
    setError('');
    createVenue.mutate(
      {
        name: form.name.trim(),
        address: form.address.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        zipCode: form.zipCode.trim() || undefined,
        country: form.country.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        contactId: form.contactId || undefined,
        capacity: form.capacity ? parseInt(form.capacity, 10) : undefined,
        priceRange: form.priceRange.trim() || undefined,
        notes: form.notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          onCreated();
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to create venue';
          setError(message);
        },
      },
    );
  }, [form, createVenue, onCreated]);

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Create Venue</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="create-venue-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Name *
          </label>
          <input
            id="create-venue-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Venue name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="create-venue-address"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Address
          </label>
          <GoogleAddressAutocomplete
            id="create-venue-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onAddressSelect={(fields) =>
              setForm((f) => ({
                ...f,
                city: fields.city || f.city,
                state: fields.state || f.state,
                zipCode: fields.zipCode || f.zipCode,
                country: fields.country || f.country,
              }))
            }
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="create-venue-city"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              City
            </label>
            <input
              id="create-venue-city"
              type="text"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="create-venue-state"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              State
            </label>
            <input
              id="create-venue-state"
              type="text"
              value={form.state}
              onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="create-venue-zip"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              ZIP Code
            </label>
            <input
              id="create-venue-zip"
              type="text"
              value={form.zipCode}
              onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="create-venue-country"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Country
            </label>
            <input
              id="create-venue-country"
              type="text"
              value={form.country}
              onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="create-venue-contact"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Contact
          </label>
          <select
            id="create-venue-contact"
            value={form.contactId}
            onChange={(e) => setForm((f) => ({ ...f, contactId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select contact (optional)</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="create-venue-phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone
            </label>
            <PhoneInput
              id="create-venue-phone"
              value={form.phone}
              onChange={(phone) => setForm((f) => ({ ...f, phone }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="create-venue-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="create-venue-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="create-venue-capacity"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Capacity
            </label>
            <input
              id="create-venue-capacity"
              type="number"
              value={form.capacity}
              onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
              placeholder="Optional"
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="create-venue-price"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Price Range
            </label>
            <input
              id="create-venue-price"
              type="text"
              value={form.priceRange}
              onChange={(e) => setForm((f) => ({ ...f, priceRange: e.target.value }))}
              placeholder="e.g. $$-$$$"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="create-venue-notes"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Notes
          </label>
          <textarea
            id="create-venue-notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Optional"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleCreate}
          disabled={!isValid || createVenue.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createVenue.isPending ? 'Creating...' : 'Create Venue'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
